import { CommonModule, CurrencyPipe, LowerCasePipe } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CountdownComponent } from '@components/countdown/countdown.component';
import { RaffleInstantLogoComponent } from '@components/raffle-instant-badge/raffle-instant-logo.component';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import {
  BadgeLabel,
  ClosedStatus,
  ColorVersion,
  ProjectCode,
  RaffleIgamingGameMechanic,
  RaffleInternalType,
  RaffleSimple,
  RelativeTime
} from '@core/models';
import { ProjectDataService } from '@core/services';
import { DateFormatterService } from '@core/services/date-formatter/date-formatter.service';
import { DateParserService } from '@core/services/date-parser/date-parser.service';
import { DateUnifierPipe } from '@shared/datetime/custom-pipes/date-unifier/date-unifier.pipe';
import { TzDatePipe } from '@shared/datetime/custom-pipes/tz-date/tz-date.pipe';
import { UkTimezonePipe } from '@shared/datetime/custom-pipes/uk-timezone/uk-timezone.pipe';

@Component({
  selector: 'app-raffle-card',
  templateUrl: './raffle-card.component.html',
  styleUrls: ['./raffle-card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DateUnifierPipe,
    CurrencyPipe,
    LowerCasePipe,
    UkTimezonePipe,
    TzDatePipe,
    RaffleInstantLogoComponent,
    CountdownComponent,
    SvgIconComponent
  ]
})
export class RaffleCardComponent implements OnChanges {
  @Input() raffle: RaffleSimple;
  @Input() whiteSection: boolean = true;

  timeVersion: string;
  version: ColorVersion;
  price: string;
  originalPrice: string;
  sliderValue: number;
  currency: string;
  isMixed: boolean;
  showCardBadge = false;
  projectCode: ProjectCode;
  badgeLabel: BadgeLabel;
  closedText: string;
  navigationUrl: string;
  projectCodes = ProjectCode;
  closedStatus = ClosedStatus;
  badgeTime: string;
  timeFormat: string;

  readonly relativeTime = RelativeTime;
  readonly igamingMechanics = RaffleIgamingGameMechanic;

  constructor(private projectDataService: ProjectDataService) {}

  get isInstantWin(): boolean {
    return (
      this.raffle.instant_win_count > 0 &&
      this.raffle.category_ids.includes('instant-wins') &&
      this.raffle.internal_raffle_type !== RaffleInternalType.IGaming
    );
  }

  get isIgaming(): boolean {
    return this.raffle.internal_raffle_type == RaffleInternalType.IGaming;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.raffle?.currentValue) {
      this.setProjectCode();
      this.setCardData();
    }
  }

  private setProjectCode() {
    this.projectCode = this.projectDataService.get('project');
    this.navigationUrl =
      this.projectCode === ProjectCode.SevenDays ? '/product' : '/competition';
  }

  private setCardData() {
    const soldOut =
      !this.raffle.is_open &&
      this.raffle.current_entries >= this.raffle.max_entries;

    if (this.raffle.is_open || soldOut) {
      this.badgeLabel =
        this.isInstantWin || this.raffle.is_without_draw
          ? BadgeLabel.Close
          : BadgeLabel.Draw;
      this.badgeTime =
        this.badgeLabel === BadgeLabel.Close
          ? this.raffle.end_at
          : this.raffle.result_at;
      this.timeVersion = DateFormatterService.drawRelativeTimeVersion(
        this.badgeTime
      );
      this.timeFormat =
        DateParserService.parse(this.badgeTime)?.getMinutes() === 0
          ? 'ha'
          : 'h:mma';
    }

    if (!this.raffle.is_open) {
      this.closedText =
        (soldOut
          ? ClosedStatus.Sold
          : this.isIgaming
            ? ClosedStatus.Closed
            : ClosedStatus.ClosedCompt) + '!';
    }

    if (this.isInstantWin) {
      this.version = ColorVersion.Black;
    } else if (this.isIgaming) {
      this.version = ColorVersion.Translucent;
    } else {
      this.version = this.whiteSection ? ColorVersion.Grey : ColorVersion.White;
    }

    this.price = this.raffle.offers[0].price;
    this.originalPrice = this.raffle.offers[0].original_price_value_frontend;
    this.currency = this.raffle.offers[0].currency;
    this.sliderValue = Math.floor(
      (this.raffle.current_entries / this.raffle.max_entries) * 100
    );
  }
}
